import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Separator } from "../../ui/separator";
import {
    Building2,
    Users,
    Monitor,
    Key,
    Trash2,
    Copy,
    Eye,
    EyeOff,
    Check
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import { useToast } from "../../../hooks/use-toast";
import { TenantData } from "./interface";
import useUserStore from "../../../services/userStore";
import { deleteTenant, fetchTenants, generateApiKey } from "./Api/tenantDetailsapi";

export default function TenantDetailsPage() {
    const [tenantData, setTenantData] = useState<TenantData[]>([]);
    const [deleteApiKeyDialog, setDeleteApiKeyDialog] = useState(false);
    const [deleteTenantDialog, setDeleteTenantDialog] = useState(false);
    const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();
    const userEmail = useUserStore((state) => state.user.email);
    const [timers, setTimers] = useState<Record<string, number>>({});

    const handleCopyApiKey = (apiKey: string) => {
        navigator.clipboard.writeText(apiKey);
        setIsCopied(true);

        setTimeout(() => {
            setIsCopied(false);
        }, 2000);

        toast({
            title: "Agent Key Copied",
            description: "The agent key has been copied to your clipboard.",
        });
    };

    const toggleApiKeyVisibility = () => {
        setIsApiKeyVisible(!isApiKeyVisible);
    };

    const handleGenerateApiKey = async (tenant: TenantData) => {
        try {
            const response = await generateApiKey(tenant.tenantName);

            const updatedTenantData = tenantData.map((el) => {
                if (el.tenantName === tenant.tenantName) {
                    console.log(response.key, response.expiredAt, el);
                    return {
                        ...el,
                        apiKey: { key: response.key, expiredAt: response.expiredAt },
                    };
                } else {
                    return el;
                }
            });

            setTenantData(updatedTenantData);

            toast({
                title: "Agent Key Generated",
                description: `New agent key has been generated for ${tenant.tenantName}.`,
            });
        } catch (error) {
            console.error("Error generating API key:", error);
            toast({
                title: "Error",
                description: "Failed to generate API key. Please try again.",
                variant: "destructive",
            });
        }
    };


    const formatCountdown = (expiry: string | null) => {
        if (!expiry) return "No key";

        const expiryDate = new Date(expiry);
        const now = new Date();
        const diffMs = expiryDate.getTime() - now.getTime();

        if (diffMs <= 0) return "Expired";

        const totalSeconds = Math.floor(diffMs / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${days}d ${hours.toString().padStart(2, "0")}h ${minutes
            .toString()
            .padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
    };



    const handleDeleteApiKeyConfirm = async (tenant: TenantData) => {

        await deleteTenant(tenant.tenantName, tenant.apiKey?.key || "");

        toast({
            title: "Agent Key Deleted",
            description: `Agent key for ${tenant.tenantName} has been revoked and is no longer valid.`,
            variant: "destructive",
        });
        setTenantData((prevTenants) =>
            prevTenants.map((t) =>
                t.tenantName === tenant.tenantName
                    ? { ...t, apiKey: null }
                    : t
            )
        );
        setDeleteApiKeyDialog(false);
    };

    const fetchTenantsDetails = async () => {
        let response = await fetchTenants();
        setTenantData(response || []);
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setTimers((prev) => {
                const updated: Record<string, number> = {};
                tenantData.forEach((tenant) => {
                    const expiry = tenant.apiKey?.expiredAt;
                    if (expiry) {
                        const expiryDate = new Date(expiry);
                        updated[tenant.domainName] = expiryDate.getTime();
                    }
                });
                return updated;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [tenantData]);

    useEffect(() => {
        fetchTenantsDetails();
    }, [userEmail]);


    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                        <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                            Tenant Details
                        </h1>
                        <p className="text-muted-foreground">
                            Manage tenant configuration and agent access
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Tenant Card */}

            {tenantData.map((tenant) => <><Card className="border-border/40">
                <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                <Building2 className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h2 className="text-xl font-semibold" data-testid="text-tenant-name">
                                        {tenant.tenantName}
                                    </h2>

                                </div>

                            </div>
                        </div>
                        <Button
                            variant="destructive"
                            onClick={() => setDeleteTenantDialog(true)}
                            className="gap-2"
                            data-testid="button-delete-tenant"
                            disabled
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete Tenant
                        </Button>
                    </div>
                </CardHeader>
                <Separator />
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Statistics Section */}
                        <Card className="border-border/40">
                            <CardHeader>
                                <CardTitle className="text-base font-semibold">Statistics</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                                            <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-muted-foreground">Computers</div>
                                            <div className="text-2xl font-bold font-mono">{tenant.numberOfComputers}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                                            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-muted-foreground">Users</div>
                                            <div className="text-2xl font-bold font-mono">{tenant.numberOfUsers}</div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Agent Key Section */}
                        <Card className="border-border/40">
                            <CardHeader>
                                <CardTitle className="text-base font-semibold">Agent Key</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {tenant.apiKey?.key ? (
                                    <>
                                        <div className="space-y-3">
                                            <div>
                                                <div className="text-xs text-muted-foreground mb-2 font-medium">Key</div>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 bg-muted px-4 py-3 rounded-lg text-base font-mono select-all">
                                                        {isApiKeyVisible ? tenant.apiKey.key : '••••••'}
                                                    </code>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={toggleApiKeyVisibility}
                                                        title={isApiKeyVisible ? "Hide agent key" : "Show agent key"}
                                                    >
                                                        {isApiKeyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={() => handleCopyApiKey(tenant.apiKey?.key!)}
                                                        title="Copy agent key"
                                                        data-testid="button-copy-api-key"
                                                    >
                                                        {isCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                                    </Button>
                                                </div>
                                            </div>

                                            {tenant.apiKey.expiredAt && (
                                                <div>
                                                    <div className="text-xs text-muted-foreground mb-2 font-medium">Expires In</div>
                                                    <Badge
                                                        variant="secondary"
                                                        className={`font-mono text-sm px-3 py-1.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`}
                                                    >
                                                        {formatCountdown(tenant.apiKey.expiredAt)}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>

                                        <Button
                                            variant="destructive"
                                            onClick={() => setDeleteApiKeyDialog(true)}
                                            className="w-full gap-2"
                                            data-testid="button-delete-api-key"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete Agent Key
                                        </Button>
                                    </>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="text-center py-6 text-muted-foreground">
                                            <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <div className="font-medium">No agent key configured</div>
                                            <div className="text-sm">Generate a key to enable agent access</div>
                                        </div>
                                        <Button
                                            variant="default"
                                            onClick={() => handleGenerateApiKey(tenant)}
                                            className="w-full gap-2"
                                            data-testid="button-generate-api-key"
                                        >
                                            <Key className="w-4 h-4" />
                                            Generate Agent Key
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>

                {/* Delete Agent Key Confirmation Dialog */}
                < AlertDialog open={deleteApiKeyDialog} onOpenChange={setDeleteApiKeyDialog} >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Agent Key</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to delete the agent key for "{tenant.tenantName}"?
                                This action cannot be undone and will immediately revoke access for all agents using this key.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => handleDeleteApiKeyConfirm(tenant)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Delete Agent Key
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Delete Tenant Confirmation Dialog */}
                <AlertDialog open={deleteTenantDialog} onOpenChange={setDeleteTenantDialog}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to permanently delete the tenant "{tenant.tenantName}"?
                                This action cannot be undone and will remove all associated data, users, and devices.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => { }}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Delete Tenant
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </>
            )}




        </div>
    );
}